import {
  RESERVATION_TYPE,
  normalizeType,
} from "../../jsx/components/ResortBooking/reservationViewUtils";

/** Pure helpers for composing resort availability from Firebase dumps. */

/** Only confirmed and checked-in stays count toward sold inventory. */
export const SOLD_RESERVATION_STATUSES = [
  RESERVATION_TYPE.CONFIRMED,
  RESERVATION_TYPE.CHECKED_IN,
];

export const shouldCountReservation = (reservation) => {
  const type = normalizeType(reservation?.reservationType);
  return SOLD_RESERVATION_STATUSES.includes(type);
};

export const isReservationActiveOnDay = (reservation, day) => {
  const checkIn = new Date(reservation.checkIn);
  const checkOut = new Date(reservation.checkOut);
  const dayDate = new Date(day);

  if (
    Number.isNaN(checkIn.getTime()) ||
    Number.isNaN(checkOut.getTime()) ||
    Number.isNaN(dayDate.getTime())
  ) {
    return false;
  }

  const dayMs = Date.UTC(
    dayDate.getFullYear(),
    dayDate.getMonth(),
    dayDate.getDate()
  );
  const inMs = Date.UTC(
    checkIn.getFullYear(),
    checkIn.getMonth(),
    checkIn.getDate()
  );
  const outMs = Date.UTC(
    checkOut.getFullYear(),
    checkOut.getMonth(),
    checkOut.getDate()
  );

  return dayMs >= inMs && dayMs < outMs;
};

export const getDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end < start
  ) {
    return [];
  }

  const days = [];
  const cursor = new Date(
    Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
  );
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

  while (cursor.getTime() <= endUtc) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, "0");
    const d = String(cursor.getUTCDate()).padStart(2, "0");
    days.push(`${y}-${m}-${d}`);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return days;
};

const makeKey = (...parts) => parts.join("__");

const addToMap = (map, key, quantity) => {
  map.set(key, (map.get(key) || 0) + quantity);
};

/**
 * Build the same availability shape the Resorts Dashboard uses.
 */
export const buildAvailabilitySnapshot = ({
  hotelsData = [],
  roomsData = [],
  roomsWithPlans = [],
  reservationsData = [],
  days = [],
  scopedHotelId = "",
} = {}) => {
  let hotels = (hotelsData || []).filter((hotel) => hotel.isApproved === true);

  if (scopedHotelId) {
    const scopedHotel = (hotelsData || []).find(
      (hotel) => hotel.id === scopedHotelId
    );
    hotels = scopedHotel
      ? [scopedHotel]
      : [{ id: scopedHotelId, name: scopedHotelId }];
  }

  const hotelMap = new Map(hotels.map((hotel) => [hotel.id, hotel]));
  const hotelIds = new Set(hotels.map((hotel) => hotel.id));
  const activeRooms = (roomsData || []).filter(
    (room) => hotelIds.has(room.hotel_id) && room.isApproved === true
  );

  const soldByHotelDate = new Map();
  const soldByPlanDate = new Map();
  const activeReservations = (reservationsData || []).filter(
    (reservation) =>
      hotelIds.has(reservation.hotelId) && shouldCountReservation(reservation)
  );

  activeReservations.forEach((reservation) => {
    days.forEach((day) => {
      if (!isReservationActiveOnDay(reservation, day)) return;

      (reservation.roomDetails || []).forEach((roomDetail) => {
        const quantity = Number(roomDetail.quantity || 0);
        if (!quantity) return;

        addToMap(soldByHotelDate, makeKey(reservation.hotelId, day), quantity);
        addToMap(
          soldByPlanDate,
          makeKey(
            reservation.hotelId,
            roomDetail.id,
            roomDetail.planId,
            day
          ),
          quantity
        );
      });
    });
  });

  const roomPlans = (roomsWithPlans || []).flatMap((room) =>
    (room.plans || []).map((plan) => {
      const total = Number(plan.totalPlan || plan.roomsAvailable || 0);
      const planName = plan.name || plan.planName || "Default Plan";

      return {
        hotelId: room.hotel_id,
        hotelName: hotelMap.get(room.hotel_id)?.name || room.hotel_id,
        roomId: room.id,
        roomName: room.name || "Unnamed Room",
        planId: plan.id,
        planName,
        total,
        daily: days.map((day) => {
          const sold = Number(
            soldByPlanDate.get(
              makeKey(room.hotel_id, room.id, plan.id, day)
            ) || 0
          );
          return {
            date: day,
            total,
            sold,
            available: Math.max(total - sold, 0),
          };
        }),
      };
    })
  );

  const hotelsAvailability = hotels.map((hotel) => {
    const hotelRoomPlans = roomPlans.filter(
      (roomPlan) => roomPlan.hotelId === hotel.id
    );
    const total = hotelRoomPlans.reduce(
      (sum, roomPlan) => sum + Number(roomPlan.total || 0),
      0
    );

    return {
      ...hotel,
      total,
      roomsCount: activeRooms.filter((room) => room.hotel_id === hotel.id)
        .length,
      plansCount: hotelRoomPlans.length,
      daily: days.map((day) => {
        const sold = Number(soldByHotelDate.get(makeKey(hotel.id, day)) || 0);
        return {
          date: day,
          total,
          sold,
          available: Math.max(total - sold, 0),
        };
      }),
    };
  });

  return {
    hotels: hotelsAvailability,
    roomPlans,
    roomsCount: activeRooms.length,
    days,
  };
};
